import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { Form, Input, Select, InputNumber, Switch, Button, Row, Col, Drawer, Collapse, Divider, Tag, Space, Upload, Image, message } from "antd";
import { PlusOutlined, RobotOutlined, UploadOutlined } from "@ant-design/icons";
import api from "../../../../api/enhancedApi";
import { agentGovernanceApi } from "../../../../api/v3/agentGovernanceApi";
import { uploadFile } from "../../../../api/attachmentApi";
import { formatDateTime } from "../../../../utils/dateUtils";
import AIFormFillModal from "../../../../components/common/AIFormFillModal";
import HourMinuteInput, { normalizeBlockTimeMinutes } from "../../../../components/TimeInput/HourMinuteInput";
import AircraftSelector from "../../../../components/common/AircraftSelector";
import CopyableItemList from "./CopyableItemList";

const { TextArea } = Input;

const { Option } = Select;

const WIND_MONTH_OPTIONS = Array.from({ length: 12 }, (_, index) => ({
    value: index + 1,
    label: `${index + 1}月`,
}));

export const normalizeBlockTimeField = (values) => {
    if (!values || values.block_time_minutes === undefined || values.block_time_minutes === null) {
        return values;
    }

    return {
        ...values,
        block_time_minutes: normalizeBlockTimeMinutes(values.block_time_minutes),
    };
};

/**
 * 航路表单组件 - 符合IATA/ICAO标准
 * 包含Drawer和AI智能填写功能
 * readOnly=true 时为查看详情模式：字段布局与编辑一致，但全部禁用输入、隐藏保存入口
 */
const FlightRouteForm = forwardRef(
    (
        {
            initialValues = {},
            onSubmit,
            onCancel,
            isEdit = false,
            loading = false,
            hideActions = false,
            readOnly = false,
            // Drawer相关props
            visible = false,
            onClose,
            title,
        },
        ref,
    ) => {
        const [form] = Form.useForm();
        const watchedOverflightCountries = Form.useWatch("overflight_countries", form);
        const watchedAlternateAirports = Form.useWatch("alternate_airports", form);
        const overflightCountries = watchedOverflightCountries ?? initialValues?.overflight_countries;
        const alternateAirports = watchedAlternateAirports ?? initialValues?.alternate_airports;
        const [tags, setTags] = useState([]);
        const [customTags, setCustomTags] = useState(initialValues?.tags || []);
        const [newTag, setNewTag] = useState("");
        const [uploading, setUploading] = useState(false);

        // AI填写相关状态
        const [aiModalVisible, setAiModalVisible] = useState(false);
        const [formSchema, setFormSchema] = useState(null);

        // 暴露表单方法给父组件
        useImperativeHandle(
            ref,
            () => ({
                submit: () => {
                    form.submit();
                },
                reset: () => {
                    form.resetFields();
                },
            }),
            [form],
        );

        // 航路类型选项
        const routeTypeOptions = [
            { value: "NORMAL", label: "正常航路 (Normal)" },
            { value: "EMERGENCY", label: "紧急航路 (Emergency)" },
            { value: "WEATHER", label: "天气备用 (Weather)" },
            { value: "SEASONAL", label: "季节性航路 (Seasonal)" },
        ];

        useEffect(() => {
            loadTags();
            if (!readOnly) {
                loadFormSchema();
            }

            if (initialValues && Object.keys(initialValues).length > 0) {
                // 编辑模式：设置现有数据
                form.setFieldsValue({
                    ...normalizeBlockTimeField(initialValues),
                    tags: undefined, // tags单独处理
                });
                setCustomTags(initialValues.tags || []);
            } else {
                // 新建模式：重置表单为默认值
                form.resetFields();
                form.setFieldsValue({
                    is_active: true,
                    is_default: false,
                });
                setCustomTags([]);
            }
        }, [initialValues, form, readOnly]);

        // 监听抽屉的打开/关闭状态，关闭时清空AI状态
        useEffect(() => {
            if (visible === false) {
                // 抽屉关闭时清空AI状态
                const persistenceKey = `ai-form-fill-v2-flight_route-${isEdit ? "UPDATE" : "CREATE"}`;
                try {
                    localStorage.removeItem(persistenceKey);
                } catch (error) {
                    console.warn("清除AI填写状态失败:", error);
                }
            }
        }, [visible, isEdit]);

        const loadTags = async () => {
            try {
                const response = await api.flightRoute.getAllTags();
                setTags(response.data?.tags || []);
            } catch (error) {
                console.error("加载标签失败:", error);
            }
        };

        // 加载表单Schema
        const loadFormSchema = async () => {
            try {
                const response = await agentGovernanceApi.getMasterDataFormSchema("flight_route", isEdit ? "UPDATE" : "CREATE");
                setFormSchema(response.data.schema);
            } catch (error) {
                console.error("加载表单Schema失败:", error);
            }
        };

        // 处理AI填写成功
        const handleAIFillSuccess = (aiData) => {
            try {
                // 将AI解析的数据填入表单
                const formValues = normalizeBlockTimeField({ ...aiData });

                // 处理tags字段
                if (formValues.tags) {
                    setCustomTags(formValues.tags);
                    delete formValues.tags;
                }

                // 设置表单值
                form.setFieldsValue(formValues);

                message.success("AI填写成功！请检查并确认表单内容");
            } catch (error) {
                console.error("应用AI填写结果失败:", error);
                message.error("应用AI填写结果失败");
            }
        };

        // 打开AI填写Modal
        const handleOpenAIModal = () => {
            if (!formSchema) {
                message.warning("表单Schema未加载完成，请稍后重试");
                return;
            }
            setAiModalVisible(true);
        };

        const handleSubmit = async (values) => {
            const windMonths = Array.isArray(values.wind_months) && values.wind_months.length > 0
                ? [...new Set(values.wind_months)].sort((a, b) => a - b)
                : null;
            const formData = {
                ...normalizeBlockTimeField(values),
                tags: customTags,
                wind_months: windMonths,
                // 确保IATA码为大写
                departure_airport: values.departure_airport?.toUpperCase(),
                arrival_airport: values.arrival_airport?.toUpperCase(),
            };

            onSubmit(formData);
        };

        // 标签管理
        const addTag = () => {
            if (newTag && !customTags.includes(newTag)) {
                setCustomTags([...customTags, newTag]);
                setNewTag("");
            }
        };

        const removeTag = (tagToRemove) => {
            setCustomTags(customTags.filter((tag) => tag !== tagToRemove));
        };

        const addExistingTag = (tag) => {
            if (!customTags.includes(tag)) {
                setCustomTags([...customTags, tag]);
            }
        };

        // 图片上传处理
        const handleImageUpload = async (file) => {
            setUploading(true);
            try {
                // 使用正确的附件上传API
                const result = await uploadFile(file);
                const imageUrl = result.download_url;

                if (imageUrl) {
                    form.setFieldsValue({ route_image_url: imageUrl });
                    message.success("图片上传成功");
                }
            } catch (error) {
                console.error("图片上传失败:", error);
                message.error("图片上传失败");
            } finally {
                setUploading(false);
            }
            return false; // 阻止默认上传行为
        };

        // 如果有visible prop，则渲染Drawer包装的表单
        if (visible !== undefined) {
            return (
                <>
                    <Drawer
                        title={
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                                <span>{title || (readOnly ? "查看航路" : isEdit ? "编辑航路" : "新建航路")}</span>
                                <Space>
                                    {readOnly ? (
                                        <Button onClick={onClose || onCancel}>关闭</Button>
                                    ) : (
                                        <>
                                            <Button type="dashed" icon={<RobotOutlined />} onClick={handleOpenAIModal}>
                                                AI智能填写
                                            </Button>
                                            <Button onClick={onClose || onCancel}>取消</Button>
                                            <Button
                                                type="primary"
                                                loading={loading}
                                                onClick={() => {
                                                    // 触发表单提交
                                                    form.submit();
                                                }}
                                            >
                                                {isEdit ? "更新" : "创建"}
                                            </Button>
                                        </>
                                    )}
                                </Space>
                            </div>
                        }
                        width={1200}
                        open={visible}
                        onClose={onClose || onCancel}
                        maskClosable={readOnly}
                        closable={false}
                    >
                        <Form form={form} layout="vertical" onFinish={handleSubmit} scrollToFirstError className="flight-route-form" initialValues={{ is_active: true, is_default: false }} disabled={readOnly} requiredMark={!readOnly}>
                            {/* 录入信息：查看/编辑已有航路时展示 */}
                            {(readOnly || isEdit) && (initialValues?.created_at || initialValues?.updated_at) && (
                                <div style={{ marginBottom: 16, padding: "8px 12px", background: "#fafafa", borderRadius: 4, color: "#666", fontSize: 13 }}>
                                    <Space size="large">
                                        <span>录入日期：{formatDateTime(initialValues?.created_at)}</span>
                                        <span>最后修改：{formatDateTime(initialValues?.updated_at)}</span>
                                    </Space>
                                </div>
                            )}
                            {/* 基本信息 */}
                            <Divider orientation="left">基本信息</Divider>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item label="航路名称" name="route_name" rules={[{ required: true, message: "请输入航路名称" }]}>
                                        <Input placeholder="如: LGG-HKG-VIA-AFG" />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item
                                        label="出发机场"
                                        name="departure_airport"
                                        rules={[
                                            { required: true, message: "请输入出发机场" },
                                            { len: 3, message: "请输入3位IATA代码" },
                                        ]}
                                        tooltip="IATA三字代码"
                                    >
                                        <Input placeholder="如: LGG" maxLength={3} style={{ textTransform: "uppercase" }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item
                                        label="到达机场"
                                        name="arrival_airport"
                                        rules={[
                                            { required: true, message: "请输入到达机场" },
                                            { len: 3, message: "请输入3位IATA代码" },
                                        ]}
                                        tooltip="IATA三字代码"
                                    >
                                        <Input placeholder="如: HKG" maxLength={3} style={{ textTransform: "uppercase" }} />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item label="航路类型" name="route_type">
                                        <Select placeholder="选择航路类型" options={routeTypeOptions} />
                                    </Form.Item>
                                </Col>
                                {/* 【航段航路BH优化】航路点序列改为非必填 */}
                                <Col span={16}>
                                    <Form.Item label="航路点序列" name="route_waypoints">
                                        <TextArea placeholder="请输入航路点序列，用空格分隔，如: EBLG LNO2R LNO L607 UBIDU...（可选）" rows={2} />
                                    </Form.Item>
                                </Col>

                                {/* 飞机适配范围 */}
                                <Col span={16}>
                                    <Form.Item label="适配飞机" name="compatible_aircraft" tooltip="选择可以使用此航路的飞机，留空表示所有飞机都可使用" extra="如果不选择任何飞机，则表示所有飞机都可以使用此航路">
                                        <AircraftSelector mode="multiple" placeholder="选择适配的飞机（留空表示所有飞机都可使用）" valueType="registration" displayMode="detailed" style={{ width: "100%" }} />
                                    </Form.Item>
                                </Col>
                            </Row>

                            {/* 时间距离信息 */}
                            <Divider orientation="left">时间距离信息</Divider>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item label="轮档时间 (BH)" name="block_time_minutes" tooltip="Block Hours - 从撤轮挡到上轮挡的总时间" rules={[{ required: true, message: "请输入轮档时间" }]}>
                                        <HourMinuteInput style={{ width: "100%" }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item label="航程海里数 (NM)" name="nautical_miles" tooltip="Nautical Miles">
                                        <InputNumber placeholder="海里" min={0} style={{ width: "100%" }} addonAfter="NM" />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item label="平均风分量 (WC)" name="average_wind_component" tooltip="Wind Component - P表示顺风，M表示逆风">
                                        <Input placeholder="如: P53, M25" maxLength={10} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={16}>
                                    <Form.Item
                                        label="几月风"
                                        name="wind_months"
                                        tooltip="该航路性能数字所依据的平均风场月份；可多选 1–12 月，可不填"
                                    >
                                        <Select
                                            mode="multiple"
                                            allowClear
                                            placeholder="选择月份（可不填）"
                                            options={WIND_MONTH_OPTIONS}
                                            optionFilterProp="label"
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            {/* 选填区块：折叠面板 */}
                            <Collapse
                                ghost
                                defaultActiveKey={isEdit || readOnly ? ["fuel", "payload", "transit", "media"] : []}
                                style={{ marginTop: 8 }}
                                items={[
                                    {
                                        key: "fuel",
                                        label: <span style={{ fontWeight: 600 }}>燃油规划 (ICAO Annex 6) — 选填</span>,
                                        children: (
                                            <>
                                                <Row gutter={16}>
                                                    <Col span={8}>
                                                        <Form.Item label="所需燃油 (Block Fuel)" name="block_fuel" tooltip="飞机起飞前必须携带的总燃油量" extra="包含所有燃油组成部分的总和">
                                                            <InputNumber placeholder="如 117.3" min={0} style={{ width: "100%" }} addonAfter="吨" precision={1} step={0.1} />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={8}>
                                                        <Form.Item label="航程燃油 (Trip Fuel)" name="trip_fuel" tooltip="完成航程所需燃油">
                                                            <InputNumber placeholder="kg" min={0} style={{ width: "100%" }} addonAfter="kg" precision={1} step={0.1} />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={8}>
                                                        <Form.Item label="目的地上空燃油 (FOD)" name="fuel_over_destination" tooltip="Fuel Over Destination - 抵达目的地上空剩余燃油">
                                                            <InputNumber placeholder="kg" min={0} style={{ width: "100%" }} addonAfter="kg" precision={1} step={0.1} />
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                                <Row gutter={16}>
                                                    <Col span={8}>
                                                        <Form.Item label="应急燃油 (Contingency)" name="contingency_fuel" tooltip="应急情况燃油">
                                                            <InputNumber placeholder="kg" min={0} style={{ width: "100%" }} addonAfter="kg" precision={1} step={0.1} />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={8}>
                                                        <Form.Item label="备降燃油 (Alternate)" name="alternate_fuel" tooltip="飞往备降场燃油">
                                                            <InputNumber placeholder="kg" min={0} style={{ width: "100%" }} addonAfter="kg" precision={1} step={0.1} />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={8}>
                                                        <Form.Item label="最终储备燃油 (Final Reserve)" name="final_reserve_fuel" tooltip="最终储备燃油">
                                                            <InputNumber placeholder="kg" min={0} style={{ width: "100%" }} addonAfter="kg" precision={1} step={0.1} />
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                                <Row gutter={16}>
                                                    <Col span={8}>
                                                        <Form.Item label="附加燃油 (Additional)" name="additional_fuel" tooltip="额外携带燃油">
                                                            <InputNumber placeholder="kg" min={0} style={{ width: "100%" }} addonAfter="kg" precision={1} step={0.1} />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={8}>
                                                        <Form.Item label="滑行燃油 (Taxi Fuel)" name="taxi_fuel" tooltip="地面滑行消耗燃油">
                                                            <InputNumber placeholder="kg" min={0} style={{ width: "100%" }} addonAfter="kg" precision={1} step={0.1} />
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                            </>
                                        ),
                                    },
                                    {
                                        key: "payload",
                                        label: <span style={{ fontWeight: 600 }}>载荷和重量信息 — 选填</span>,
                                        children: (
                                            <>
                                                <Row gutter={16}>
                                                    <Col span={8}>
                                                        <Form.Item label="旅客数量 (PAX)" name="passenger_count" tooltip="Passengers">
                                                            <InputNumber placeholder="人数" min={0} style={{ width: "100%" }} addonAfter="人" />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={8}>
                                                        <Form.Item label="货物重量 (CARGO)" name="cargo_weight" tooltip="Cargo Weight">
                                                            <InputNumber placeholder="吨" min={0} style={{ width: "100%" }} addonAfter="吨" precision={2} />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={8}>
                                                        <Form.Item label="总业载 (TTL)" name="total_traffic_load" tooltip="Total Traffic Load">
                                                            <InputNumber placeholder="kg" min={0} style={{ width: "100%" }} addonAfter="kg" />
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                                <Row gutter={16}>
                                                    <Col span={8}>
                                                        <Form.Item label="基本空重 (OEW)" name="operating_empty_weight" tooltip="Operating Empty Weight">
                                                            <InputNumber placeholder="吨" min={0} style={{ width: "100%" }} addonAfter="吨" precision={2} />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={8}>
                                                        <Form.Item label="最大起飞重量 (MTOW)" name="max_takeoff_weight" tooltip="Maximum Takeoff Weight">
                                                            <InputNumber placeholder="吨" min={0} style={{ width: "100%" }} addonAfter="吨" precision={2} />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={8}>
                                                        <Form.Item label="最大着陆重量 (MLW)" name="max_landing_weight" tooltip="Maximum Landing Weight">
                                                            <InputNumber placeholder="吨" min={0} style={{ width: "100%" }} addonAfter="吨" precision={2} />
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                            </>
                                        ),
                                    },
                                    {
                                        key: "transit",
                                        label: <span style={{ fontWeight: 600 }}>过境信息和备用机场 — 选填</span>,
                                        children: (
                                            <Row gutter={16}>
                                                <Col span={12}>
                                                    <Form.Item
                                                        label="过境国家/地区"
                                                        name={readOnly ? undefined : "overflight_countries"}
                                                        tooltip={readOnly ? "完整列表可直接选中，或点复制全部" : "标签不便划选复制，下方名单可复制"}
                                                        extra={!readOnly ? <CopyableItemList items={overflightCountries} hideWhenEmpty /> : null}
                                                    >
                                                        {readOnly ? (
                                                            <CopyableItemList items={overflightCountries} />
                                                        ) : (
                                                            <Select
                                                                className="flight-route-tag-select"
                                                                mode="tags"
                                                                placeholder="输入国家/地区名称后按回车"
                                                                style={{ width: "100%" }}
                                                                tokenSeparators={[",", "，"]}
                                                                open={false}
                                                            />
                                                        )}
                                                    </Form.Item>
                                                </Col>
                                                <Col span={12}>
                                                    <Form.Item
                                                        label="备用机场"
                                                        name={readOnly ? undefined : "alternate_airports"}
                                                        tooltip={readOnly ? "完整列表可直接选中，或点复制全部" : "标签不便划选复制，下方名单可复制"}
                                                        extra={!readOnly ? <CopyableItemList items={alternateAirports} hideWhenEmpty /> : null}
                                                    >
                                                        {readOnly ? (
                                                            <CopyableItemList items={alternateAirports} />
                                                        ) : (
                                                            <Select
                                                                className="flight-route-tag-select"
                                                                mode="tags"
                                                                placeholder="输入机场代码后按回车，如 SHJ"
                                                                style={{ width: "100%" }}
                                                                tokenSeparators={[",", "，", " "]}
                                                                open={false}
                                                            />
                                                        )}
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                        ),
                                    },
                                    {
                                        key: "media",
                                        label: <span style={{ fontWeight: 600 }}>图片和标签 — 选填</span>,
                                        children: (
                                            <Row gutter={16}>
                                                <Col span={8}>
                                                    {readOnly ? (
                                                        <Form.Item label="航路图片">
                                                            {initialValues?.route_image_url ? (
                                                                <Image src={initialValues.route_image_url} alt="航路图" style={{ maxWidth: "100%", maxHeight: 240, objectFit: "contain" }} />
                                                            ) : (
                                                                <span style={{ color: "#999" }}>暂无图片</span>
                                                            )}
                                                        </Form.Item>
                                                    ) : (
                                                        <Form.Item label="航路图片" name="route_image_url">
                                                            <div>
                                                                {form.getFieldValue("route_image_url") ? (
                                                                    <div style={{ marginBottom: 8, position: "relative" }}>
                                                                        <Image
                                                                            src={form.getFieldValue("route_image_url")}
                                                                            alt="航路图预览"
                                                                            style={{ maxWidth: "100%", maxHeight: 160, objectFit: "contain", borderRadius: 4, border: "1px solid #d9d9d9" }}
                                                                        />
                                                                        <Button
                                                                            type="link"
                                                                            danger
                                                                            size="small"
                                                                            style={{ position: "absolute", top: 4, right: 4, background: "rgba(255,255,255,0.85)", borderRadius: 4 }}
                                                                            onClick={() => form.setFieldsValue({ route_image_url: null })}
                                                                        >
                                                                            移除
                                                                        </Button>
                                                                    </div>
                                                                ) : null}
                                                                <Upload.Dragger beforeUpload={handleImageUpload} showUploadList={false} accept="image/*" style={{ padding: form.getFieldValue("route_image_url") ? "8px" : "20px" }}>
                                                                    <p className="ant-upload-drag-icon">
                                                                        <UploadOutlined />
                                                                    </p>
                                                                    <p className="ant-upload-text">{uploading ? "上传中..." : form.getFieldValue("route_image_url") ? "重新上传" : "点击或拖拽图片到此区域上传"}</p>
                                                                </Upload.Dragger>
                                                            </div>
                                                        </Form.Item>
                                                    )}
                                                </Col>

                                                <Col span={16}>
                                                    <Form.Item label={readOnly ? "标签" : "自定义标签"}>
                                                        <div>
                                                            {!readOnly && (
                                                                <Space style={{ marginBottom: 8 }}>
                                                                    <Input placeholder="添加新标签" value={newTag} onChange={(e) => setNewTag(e.target.value)} onPressEnter={addTag} style={{ width: 150 }} />
                                                                    <Button icon={<PlusOutlined />} onClick={addTag} disabled={!newTag || customTags.includes(newTag)}>
                                                                        添加
                                                                    </Button>
                                                                    <span style={{ marginLeft: 16, marginRight: 8 }}>选择已有标签:</span>
                                                                    <Select placeholder="选择标签" style={{ width: 150 }} onChange={addExistingTag} value={undefined}>
                                                                        {tags
                                                                            .filter((tag) => !customTags.includes(tag))
                                                                            .map((tag) => (
                                                                                <Option key={tag} value={tag}>
                                                                                    {tag}
                                                                                </Option>
                                                                            ))}
                                                                    </Select>
                                                                </Space>
                                                            )}
                                                            <div>
                                                                {customTags.length === 0 && readOnly ? (
                                                                    <span style={{ color: "#999" }}>无标签</span>
                                                                ) : (
                                                                    customTags.map((tag) => (
                                                                        <Tag key={tag} closable={!readOnly} onClose={() => removeTag(tag)} style={{ marginBottom: 4 }}>
                                                                            {tag}
                                                                        </Tag>
                                                                    ))
                                                                )}
                                                            </div>
                                                        </div>
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                        ),
                                    },
                                ]}
                            />

                            {/* 状态和属性 */}
                            <Divider orientation="left">状态和属性</Divider>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item label="默认航路" name="is_default" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item label="启用状态" name="is_active" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>{/* 预留位置 */}</Col>
                            </Row>

                            <Row gutter={16}>
                                <Col span={24}>
                                    <Form.Item label="备注信息" name="remarks">
                                        <TextArea placeholder="请输入备注信息" rows={2} />
                                    </Form.Item>
                                </Col>
                            </Row>

                            {/* 操作按钮 - 可选显示 */}
                            {!hideActions && !readOnly && (
                                <div className="form-actions">
                                    <Space>
                                        <Button onClick={onCancel}>取消</Button>
                                        <Button type="primary" htmlType="submit" loading={loading}>
                                            {isEdit ? "更新" : "创建"}
                                        </Button>
                                    </Space>
                                </div>
                            )}
                        </Form>

                        {/* AI智能填写Modal */}
                        {!readOnly && <AIFormFillModal visible={aiModalVisible} onCancel={() => setAiModalVisible(false)} onSuccess={handleAIFillSuccess} formType="flight_route" formTypeLabel="航路" currentFormData={form.getFieldsValue()} operationType={isEdit ? "UPDATE" : "CREATE"} title="AI智能填写航路表单" />}
                    </Drawer>
                </>
            );
        }

        // 否则渲染普通表单
        return (
            <div>
                <Form form={form} layout="vertical" onFinish={handleSubmit} scrollToFirstError className="flight-route-form" initialValues={{ is_active: true, is_default: false }}>
                    {/* AI填写按钮 */}
                    <div style={{ marginBottom: 16, textAlign: "right" }}>
                        <Button type="dashed" icon={<RobotOutlined />} onClick={handleOpenAIModal} style={{ marginRight: 8 }}>
                            AI智能填写
                        </Button>
                    </div>

                    {/* 基本信息 */}
                    <Divider orientation="left">基本信息</Divider>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item label="航路名称" name="route_name" rules={[{ required: true, message: "请输入航路名称" }]}>
                                <Input placeholder="如: LGG-HKG-VIA-AFG" />
                            </Form.Item>
                        </Col>
                        {/* 其他表单字段保持不变... */}
                    </Row>

                    {/* 操作按钮 - 可选显示 */}
                    {!hideActions && (
                        <div className="form-actions">
                            <Space>
                                <Button onClick={onCancel}>取消</Button>
                                <Button type="primary" htmlType="submit" loading={loading}>
                                    {isEdit ? "更新" : "创建"}
                                </Button>
                            </Space>
                        </div>
                    )}
                </Form>

                {/* AI智能填写Modal */}
                <AIFormFillModal visible={aiModalVisible} onCancel={() => setAiModalVisible(false)} onSuccess={handleAIFillSuccess} formType="flight_route" formTypeLabel="航路" currentFormData={form.getFieldsValue()} operationType={isEdit ? "UPDATE" : "CREATE"} title="AI智能填写航路表单" />
            </div>
        );
    },
);

export default FlightRouteForm;
